import React, { ReactElement, useRef, useState, useEffect } from 'react'
import styled, { keyframes } from 'styled-components'

import { PrimaryButton, SecondaryButton } from '../components/Button'
import { Container } from '../components/PayInvoice'
import { Chevron } from '../icons/Chevron'
import Upload from '../icons/Upload'
import { RADOM_COLORS } from '../util/Constants'
import ID from '../icons/ID'
import Selfie from '../icons/Selfie'

import * as faceapi from 'face-api.js'
import Checkbox from '../components/Checkbox'

const rotate = keyframes`
  100% { transform: rotate(1turn); }
`

const LoaderWrapper = styled.div`
  width: 80px;
  aspect-ratio: 1;
  border-radius: 50%;
  background:
    radial-gradient(farthest-side, ${RADOM_COLORS.NEW_BLUE} 94%, #0000) top/8px 8px no-repeat,
    conic-gradient(#0000 30%, ${RADOM_COLORS.NEW_BLUE});
  -webkit-mask: radial-gradient(farthest-side, #0000 calc(100% - 8px), #000 0);
  animation: ${rotate} 1s infinite linear;
`

enum KycProgress {
  INITIAL,
  CHOICE,
  DOCUMENT,
  SELFIE,
}

enum DocumentType {
  PASSPORT = 'passport',
  ID_CARD = 'id_card',
  DRIVER_LICENSE = 'driver_license'
}

enum PaymentStatus {
  INITIAL = 'initial',
  KYC = 'kyc',
  WAITING = 'waiting',
  SUCCESS = 'success',
  ERROR = 'error'
}

interface IProps {
  onChange: (value: any) => void
  onSubmit: () => void
  onBack: () => void
  kycTips?: string
  errorMessage?: string
  minHeight?: number
  minWidth?: number
  setOpenbankPayStatus: React.Dispatch<React.SetStateAction<PaymentStatus>>
}

const KycView = ({ setOpenbankPayStatus, ...props }: IProps): ReactElement => {
  const videoRef = useRef(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const [countdown, setCountdown] = useState<number | null>(null)
  const countdownRef = useRef<NodeJS.Timeout | null>(null)
  const countdownValueRef = useRef<number>(3)
  const stableStartRef = useRef<number | null>(null)
  const [modelsLoaded, setModelsLoaded] = useState(false)
  const [progressValue, setProgressValue] = useState(0)
  const [documentType, setDocumentType] = useState<DocumentType>()
  const [documentTypeState, setDocumentTypeState] = useState<DocumentType>()

  const [kycData, setKycData] = useState({
    selfieImage: '',
    selfieImageFile: null as File | null,
    passportImage: '',
    passportImageFile: null as File | null,
    idcardImage: '',
    idcardImageFile: null as File | null
  })

  useEffect(() => {
    // M.AutoInit()
    loadModels()
  }, [])

  const startVideo = async (): Promise<void> => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: {} })
      if (videoRef.current) videoRef.current.srcObject = stream
    } catch (error) {
      console.error('Error accessing webcam:', error)
    }
  }

  const stopVideo = (): void => {
    const stream = videoRef.current.srcObject as MediaStream
    const tracks = stream.getTracks()
    tracks.forEach((track) => {
      track.stop()
    })
  }

  const toBase64 = async (file: File): Promise<string> =>
    await new Promise((resolve, reject) => {
      if (!(file instanceof Blob)) {
        return reject(new TypeError('Provided input is not a Blob or File.'))
      }

      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => {
        const result = reader.result as string
        const base64 = result.split(',')[1]
        resolve(base64)
      }
      reader.onerror = (error) => reject(error)
    })

  const loadModels = async (): Promise<void> => {
    try {
      await faceapi.nets.tinyFaceDetector.loadFromUri('/models')
      setModelsLoaded(true) // 设置模型加载完成
      console.log('Models loaded')
    } catch (error) {
      console.log(error)
      console.error('Error loading models:', error)
    }
  }

  const onPlay = async (): Promise<void> => {
    if (!modelsLoaded || !videoRef.current || videoRef.current.paused || videoRef.current.ended) {
      setTimeout(onPlay, 100)
      return
    }

    const options = new faceapi.TinyFaceDetectorOptions({ inputSize: 128, scoreThreshold: 0.5 })
    const result = await faceapi.detectSingleFace(videoRef.current, options)
    const score = result?.score ?? 0
    setProgressValue(score * 100)

    if (score > 0.6) {
      if (countdown === null && countdownRef.current === null) {
        countdownValueRef.current = 3
        setCountdown(3)
        countdownRef.current = setInterval(() => {
          countdownValueRef.current -= 1
          if (countdownValueRef.current > 0) {
            setCountdown(countdownValueRef.current)
          } else {
            clearInterval(countdownRef.current!)
            countdownRef.current = null
            setCountdown(null)

            const canvas = document.createElement('canvas')
            const video = videoRef.current as HTMLVideoElement
            if (!canvas || !video) return
            canvas.width = video.videoWidth
            canvas.height = video.videoHeight
            const ctx = canvas.getContext('2d')
            if (ctx) {
              ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
              canvas.toBlob((blob) => {
                if (blob) {
                  const file = new File([blob], 'selfie.png', { type: 'image/png' })
                  const tempUrl = URL.createObjectURL(file)
                  setProgressValue(0)

                  toBase64(file).then((d) => {
                    setKycData({ ...kycData, selfieImage: tempUrl, selfieImageFile: file })
                    props.onChange({ imageFile: d, kycType: KycProgress.SELFIE })
                    stopVideo()
                  })
                }
              }, 'image/png')
            }
          }
        }, 1000)
      }
    } else {
      // 中断倒计时
      if (countdownRef.current) {
        clearInterval(countdownRef.current)
        countdownRef.current = null
      }
      setCountdown(null)
      countdownValueRef.current = 3
    }

    if (!kycData.selfieImage) {
      setTimeout(onPlay, 100)
    }
  }

  const onFileChange = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const fileList = event.target.files
    const file = fileList?.[0]

    if (!file?.type?.startsWith('image')) return

    const toBase64 = async (file: File): Promise<string> =>
      await new Promise((resolve, reject) => {
        if (!(file instanceof Blob)) {
          return reject(new TypeError('Provided input is not a Blob or File.'))
        }

        const reader = new FileReader()
        reader.readAsDataURL(file)
        reader.onload = () => {
          const result = reader.result as string
          const base64 = result.split(',')[1]
          resolve(base64)
        }
        reader.onerror = (error) => reject(error)
      })

    try {
      const base64String = await toBase64(file)
      const imageUrl = URL.createObjectURL(file)

      if (currentProgress === KycProgress.DOCUMENT) {
        props.onChange({ imageFile: base64String, kycType: KycProgress.DOCUMENT, documentType })
        if (documentType === DocumentType.ID_CARD) {
          setKycData({
            ...kycData,
            idcardImage: imageUrl,
            idcardImageFile: file
          })
        }
        if (documentType === DocumentType.PASSPORT) {
          setKycData({
            ...kycData,
            passportImage: imageUrl,
            passportImageFile: file
          })
        }
      }
    } catch (err) {
      console.error('Failed to convert image to base64:', err)
    } finally {
    }
  }

  const back = (): void => {
    if (currentProgress === KycProgress.SELFIE) {
      setCurrentProgress(KycProgress.DOCUMENT)
    }
    if (currentProgress === KycProgress.DOCUMENT) {
      setDocumentType(undefined)
      setDocumentTypeState(undefined)
      setCurrentProgress(KycProgress.INITIAL)
    }
    if (currentProgress === KycProgress.INITIAL) {
      props.onBack()
    }
  }

  const [currentProgress, setCurrentProgress] = useState<KycProgress>(KycProgress.INITIAL)

  return (
    (
      <Container
        style={{
          margin: 'initial',
          position: 'relative',
          padding: 0,
          width: props.minWidth ?? 450,
          maxWidth: '100%',
          minHeight: props.minHeight ?? 500,
          height: '100%'
        }}>
        {
          currentProgress === KycProgress.INITIAL &&
          <div
            style={{
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            {/* Scrollable Content */}
            <div
              style={{
                flex: 1,
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: 20
              }}
            >
              {/* Title and Guide */}
              <div>
                <div
                  style={{
                    color: RADOM_COLORS.BLACK,
                    fontSize: 16,
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: 20
                  }}
                >
                  <Chevron
                    onClick={back}
                    style={{
                      cursor: 'pointer',
                      minWidth: 20,
                      transform: 'rotate(90deg)'
                    }}
                  />
                  Let's get your KYC verified
                </div>
              </div>

              {/* Info Box */}
              {
                props.kycTips &&
                <div
                  style={{
                    background: RADOM_COLORS.INFO_LIGHT,
                    padding: 20,
                    color: RADOM_COLORS.NEW_BLUE,
                    borderRadius: 10,
                    fontSize: 14
                  }}
                >
                  {props.kycTips}
                </div>
              }

              {/* Steps */}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  padding: 10,
                  gap: 20
                }}
              >
                {/* Step 1 */}
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center'
                  }}
                >
                  <div
                    style={{
                      borderRadius: '50%',
                      background: RADOM_COLORS.GRAY_DARK,
                      padding: 10,
                      height: 50,
                      width: 50,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}
                  >
                    <ID style={{ height: 20 }} />
                  </div>
                  <div
                    style={{
                      marginLeft: 10,
                      fontSize: 14
                    }}
                  >
                    <div
                      style={{
                        color: RADOM_COLORS.GRAY_DARKER,
                        marginBottom: 5
                      }}
                    >
                      Step 1
                    </div>
                    <div>Provide identity document</div>
                  </div>
                </div>

                {/* Step 2 */}
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center'
                  }}
                >
                  <div
                    style={{
                      borderRadius: '50%',
                      background: RADOM_COLORS.GRAY_DARK,
                      padding: 10,
                      height: 50,
                      width: 50,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}
                  >
                    <Selfie style={{ height: 13 }} />
                  </div>
                  <div
                    style={{
                      marginLeft: 10,
                      fontSize: 14
                    }}
                  >
                    <div
                      style={{
                        color: RADOM_COLORS.GRAY_DARKER,
                        marginBottom: 5
                      }}
                    >
                      Step 2
                    </div>
                    <div>Get ready for a live face scan</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Sticky Bottom Button */}
            <div
              style={{
                position: 'sticky',
                bottom: 0,
                padding: 20
              }}
            >
              <PrimaryButton
                style={{
                  width: '100%'
                }}
                onClick={() => {
                  setCurrentProgress(KycProgress.DOCUMENT)
                }}
              >
                Ready to start
              </PrimaryButton>
            </div>
          </div>
        }

        {
          currentProgress === KycProgress.DOCUMENT && <>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 20
            }}>
              <div

                style={{
                  flex: 1,
                  overflowY: 'auto',
                  display: 'flex',
                  gap: 20,
                  flexDirection: 'column'
                }}>
                <div>
                  <div style={{
                    color: RADOM_COLORS.BLACK,
                    fontSize: 16,
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    <Chevron
                      onClick={back}
                      style={{
                        cursor: 'pointer',
                        minWidth: 20,
                        transform: 'rotate(90deg)'
                      }}
                    />
                    Upload your ID Card/Passport
                  </div>

                </div>
                {
                  props.kycTips &&
                  <div
                    style={{
                      background: RADOM_COLORS.INFO_LIGHT,
                      padding: 20,
                      color: RADOM_COLORS.NEW_BLUE,
                      borderRadius: 10,
                      fontSize: 14,
                      marginBottom: 30
                    }}
                  >
                    {props.kycTips}
                  </div>
                }
                {
                  (documentTypeState === DocumentType.PASSPORT || documentTypeState === DocumentType.ID_CARD) && <>
                    <div style={{
                      border: '1px dashed #ccc',
                      padding: 20,
                      borderRadius: 10,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}>
                      {
                        !(kycData.passportImage || kycData.idcardImage) && <SecondaryButton
                          style={{
                            fontSize: 14,
                            width: '100%',
                            height: 100,
                            backgroundColor: 'transparent',
                            border: 'none',
                            color: RADOM_COLORS.GRAY_DARKEST,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: 0,
                            gap: 5,
                            position: 'sticky'
                          }}>
                          <label style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}>
                            {kycData.passportImage || 'Upload ID card/Passport'}
                            <Upload stroke={RADOM_COLORS.GRAY_DARKEST} style={{ width: 18, height: 18 }} />
                            <input
                              type="file"
                              accept=".png, .jpg, .jpeg"
                              onChange={onFileChange}
                              style={{
                                opacity: 0,
                                cursor: 'pointer',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0
                              }} // Hide the file input
                            />
                          </label>
                        </SecondaryButton>
                      }
                      {
                        kycData.passportImage && <img
                          onClick={() => {
                            setKycData({ ...kycData, passportImage: '', passportImageFile: null })
                          }}
                          src={kycData.passportImage}
                          style={{
                            width: '100%',
                            height: 'auto',
                            borderRadius: 10
                          }}
                        />
                      }
                      {
                        kycData.idcardImage && <img
                          onClick={() => {
                            setKycData({ ...kycData, idcardImage: '', idcardImageFile: null })
                          }}
                          src={kycData.idcardImage}
                          style={{
                            width: '100%',
                            height: 'auto',
                            borderRadius: 10
                          }}
                        />
                      }
                    </div>

                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 10,
                      fontSize: 14,
                      marginTop: 20,
                      color: RADOM_COLORS.INPUT_PLACEHOLDER
                    }}>
                      <div
                        style={{
                          marginTop: 5,
                          marginBottom: 5,
                          color: RADOM_COLORS.GRAY4
                        }}>
                        Guide for ID Card Scan
                      </div>
                      <div>
                        1. Make sure your uploaded image is clear and the entire document is visible.
                      </div>
                      <div>
                        2. Ensure all letters are visible and the image is not blurry
                      </div>
                      <div>
                        3. Make sure your ID document is a valid government issued ID and has not expired.
                      </div>
                    </div>
                  </>
                }

                {
                  !documentTypeState && <>
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 10,
                      paddingLeft: 10,
                      paddingRight: 10
                    }}>
                      <h4>
                        Select your document type
                      </h4>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          gap: 10,
                          alignContent: 'center',
                          justifyContent: 'space-between',
                          width: '100%',
                          background: 'white',
                          padding: 10,
                          paddingLeft: 20,
                          paddingRight: 20,
                          borderRadius: 10,
                          fontSize: 14,
                          color: RADOM_COLORS.GRAY_DARKEST
                        }}
                        onClick={() => {
                          setDocumentType(DocumentType.ID_CARD)
                        }
                        }
                      >
                        <div>ID card</div>
                        <Checkbox isRadio checked={documentType === DocumentType.ID_CARD} />
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          gap: 10,
                          alignContent: 'center',
                          justifyContent: 'space-between',
                          width: '100%',
                          background: 'white',
                          padding: 10,
                          paddingLeft: 20,
                          paddingRight: 20,
                          borderRadius: 10,
                          color: RADOM_COLORS.GRAY_DARKEST
                        }}
                        onClick={() => {
                          setDocumentType(DocumentType.PASSPORT)
                        }
                        }
                      >
                        <div>Passport</div>
                        <Checkbox isRadio checked={documentType === DocumentType.PASSPORT} />
                      </div>
                    </div>
                  </>
                }

              </div>
              <PrimaryButton
                style={{
                  position: 'sticky',
                  marginTop: 30,
                  bottom: 0,
                  width: '100%'
                }}
                disabled={!documentTypeState
                  ? !documentType
                  : !(kycData.passportImage || kycData.idcardImage)}
                onClick={() => {
                  if (documentTypeState) {
                    setCurrentProgress(KycProgress.SELFIE)
                    startVideo()
                  }
                  if (documentType) {
                    setDocumentTypeState(documentType)
                  }
                }}
              >
                {
                  !documentTypeState ? 'Next 1/3' : 'Next 2/3'
                }
              </PrimaryButton>
            </div>
          </>
        }

        {
          currentProgress === KycProgress.SELFIE && <>
            <div
              style={{
                position: 'relative',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <div style={{
                color: RADOM_COLORS.BLACK,
                fontSize: 16,
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center'

              }}>
                <Chevron
                  onClick={back}
                  style={{
                    cursor: 'pointer',
                    minWidth: 20,
                    transform: 'rotate(90deg)'
                  }}
                />
                Take your Selfie
              </div>
              <div
                style={{
                  marginTop: 5,
                  color: RADOM_COLORS.GRAY4
                }}>
                Guide for Selfie
              </div>
            </div>
            <div
              style={{
                flex: 1,
                overflowY: 'auto'
              }}
            >
              {
                props.kycTips &&
                <div
                  style={{
                    background: RADOM_COLORS.INFO_LIGHT,
                    padding: 20,
                    color: RADOM_COLORS.NEW_BLUE,
                    borderRadius: 10,
                    fontSize: 14,
                    marginBottom: 30
                  }}
                >
                  {props.kycTips}
                  {/* KYC verification will apply if you deposit more than 1K EUR per single transaction or in total for 24 hours */}
                </div>
              }

              <div style={{
                display: 'flex',
                alignContent: 'center',
                justifyContent: 'center',
                paddingTop: 20
              }}>

                <div
                  style={{
                    position: 'relative', // 设置相对定位
                    width: 300, // 容器宽度
                    height: 400, // 容器高度
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}
                >
                  {/* 外圈进度条 */}
                  <div
                    style={{
                      position: 'absolute',
                      width: '105%',
                      height: '103%',
                      borderRadius: '47%',
                      opacity: 0.7,
                      background: `conic-gradient(
      ${progressValue < 30 ? '#FF4C4C' : progressValue < 70 ? '#FFD700' : RADOM_COLORS.SUCCESS} ${progressValue - 5}%,
      rgba(245, 246, 246, 0.5) ${progressValue}%,
      rgb(245, 246, 246) ${progressValue + 5}% 100%
    )`,
                      boxShadow: `0 0 20px 5px rgba(${progressValue < 30 ? '0, 0, 0' : progressValue < 70 ? '255, 215, 0' : '0, 255, 0'}, 0.15)`,
                      filter: 'blur(1px)', // 添加轻微模糊效果
                      zIndex: 1,
                      transition: 'all 1.5s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}
                  />
                  {countdown !== null && !kycData.selfieImage && (
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      background: 'rgba(0,0,0,0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 5,
                      borderRadius: '50%',
                      color: 'white',
                      fontSize: 48,
                      fontWeight: 700
                    }}>
                      {countdown}
                    </div>
                  )}
                  {/* 视频内容 */}
                  {
                    !kycData.selfieImage && <video
                      width={500}
                      ref={videoRef}
                      onLoadedMetadata={onPlay}
                      autoPlay
                      muted
                      playsInline
                      // hidden
                      id="inputVideo"
                      // hidden
                      style={{
                        borderRadius: '47%', // 设置圆形
                        width: 300, // 确保宽度和高度一致
                        height: 400, // 确保宽度和高度一致
                        objectFit: 'cover', // 确保视频内容适配圆形
                        position: 'relative', // 确保视频在进度条内
                        zIndex: 2 // 确保视频在进度条上方
                      }}
                    />
                  }
                  {
                    kycData.selfieImage && <img
                      src={kycData.selfieImage}
                      style={{
                        width: 300,
                        height: 400,
                        borderRadius: '45%',
                        objectFit: 'cover',
                        position: 'relative',
                        zIndex: 2
                      }}
                    />
                  }
                </div>
              </div>
              <SecondaryButton
                style={{
                  display: 'block', // 设置为块级元素
                  textAlign: 'center',
                  width: '100%',
                  marginTop: 50,
                  borderRadius: 25,
                  background: RADOM_COLORS.GRAY8,
                  fontSize: 16,
                  fontWeight: 500,
                  color: 'black',
                  padding: 15,
                  border: 'none' // 确保按钮内文字居中
                }}
                onClick={() => {
                  startVideo()
                  setKycData({ ...kycData, selfieImage: '', selfieImageFile: null })
                }}
              >
                Retake
              </SecondaryButton>

            </div>
            <PrimaryButton
              style={{
                width: '100%'
              }}
              disabled={!kycData.selfieImage || (!kycData.passportImage && !kycData.idcardImage)}
              onClick={props.onSubmit}
            >
              Continue to Payment
            </PrimaryButton>
          </>
        }
      </Container>
    )
  )
}

export default KycView
